import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { expedientesAPI, formatDate } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import AdvancedFilters from './AdvancedFilters';
import useIsMobile from '../hooks/useIsMobile';

// Estilos CSS para mejorar la visibilidad del botón de eliminar
const deleteButtonStyles = `
  .btn-danger {
    transition: all 0.2s ease !important;
  }
  
  .btn-danger:hover {
    background-color: #c82333 !important;
    border-color: #bd2130 !important;
    transform: scale(1.02);
    box-shadow: 0 4px 8px rgba(220, 53, 69, 0.4) !important;
  }
  
  .btn-danger:active {
    transform: scale(0.98);
  }
  
  .btn-danger:disabled {
    opacity: 0.7;
    transform: none;
  }
  
  /* Estilos para el modal de eliminación */
  .modal {
    z-index: 1050 !important;
  }
  
  .modal-dialog {
    z-index: 1055 !important;
  }
  
  .modal-backdrop {
    z-index: 1040 !important;
  }
  
  .modal-content {
    z-index: 1055 !important;
    position: relative;
  }
  
  .modal-header,
  .modal-body,
  .modal-footer {
    position: relative;
    z-index: 1056 !important;
  }
`;

const ExpedientesList = () => {
  const { user } = useAuth();
  const canViewFinancialData = user?.rol === 'JEFE' || user?.rol === 'ADMIN';
  const hideEstadoPago = user?.rol === 'PERSONAL';
  const isMobile = useIsMobile();
  const [expedientes, setExpedientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searching, setSearching] = useState(false);
  
  // Cargar filtros desde sessionStorage (solo sesión actual: misma pestaña;
  // no persiste al cerrar navegador ni en otra PC)
  const [filters, setFilters] = useState(() => {
    const saved = sessionStorage.getItem('expedientes_filters');
    return saved ? JSON.parse(saved) : {
    search: '',
    tipoId: '',
    estadoPago: '',
    estadoTrabajo: '',
    fechaDesde: '',
    fechaHasta: '',
    montoMin: '',
    montoMax: ''
    };
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0
  });
  
  // Cargar sortConfig desde sessionStorage (solo sesión actual)
  const [sortConfig, setSortConfig] = useState(() => {
    const saved = sessionStorage.getItem('expedientes_sort');
    return saved ? JSON.parse(saved) : {
      sort: 'numero',
    order: 'desc'
    };
  });
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [expedienteToDelete, setExpedienteToDelete] = useState(null);

  const loadExpedientes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null); // Limpiar errores anteriores
      
      const params = new URLSearchParams({
        ...filters,
        page: pagination.page.toString(),
        pageSize: pagination.pageSize.toString(),
        sort: sortConfig.sort,
        order: sortConfig.order
      });

      const response = await expedientesAPI.getExpedientes(params);
      setExpedientes(response.data.expedientes);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || response.data.total || 0
      }));
    } catch (err) {
      // Solo mostrar error si no es una búsqueda en progreso
      if (!filters.search || filters.search.length < 2) {
        setError('Error al cargar expedientes');
      }
      console.error('Error:', err);
    } finally {
      setLoading(false);
      setSearching(false); // Marcar búsqueda como completada
    }
  }, [filters, pagination.page, pagination.pageSize, sortConfig]);

  useEffect(() => {
    loadExpedientes();
  }, [loadExpedientes]);

  // Guardar filtros en sessionStorage (solo sesión actual)
  useEffect(() => {
    sessionStorage.setItem('expedientes_filters', JSON.stringify(filters));
  }, [filters]);

  // Guardar sortConfig en sessionStorage (solo sesión actual)
  useEffect(() => {
    sessionStorage.setItem('expedientes_sort', JSON.stringify(sortConfig));
  }, [sortConfig]);

  const handleFilterChange = (newFilters) => {
    // Si es una búsqueda, marcar como búsqueda en progreso
    if (newFilters.search !== filters.search) {
      setSearching(true);
    }
    
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      sort: field,
      order: prev.sort === field && prev.order === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  // Función para manejar la eliminación de expedientes
  const handleDeleteClick = (expediente) => {
    setExpedienteToDelete(expediente);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!expedienteToDelete) return;

    try {
      setDeletingId(expedienteToDelete.id);
      await expedientesAPI.deleteExpediente(expedienteToDelete.id);
      
      // Recargar la lista
      await loadExpedientes();
      
      // Cerrar modal y limpiar estado
      setShowDeleteModal(false);
      setExpedienteToDelete(null);
      setDeletingId(null);
    } catch (error) {
      console.error('Error eliminando expediente:', error);
      setDeletingId(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setExpedienteToDelete(null);
    setDeletingId(null);
  };

  // Función para restaurar expedientes
  const handleRestoreClick = async (expediente) => {
    try {
      setDeletingId(expediente.id);
      await expedientesAPI.restoreExpediente(expediente.id);
      
      // Recargar la lista
      await loadExpedientes();
      
      setDeletingId(null);
    } catch (error) {
      console.error('Error restaurando expediente:', error);
      setDeletingId(null);
    }
  };



  const getEstadoPagoBadge = (expediente) => {
    const porcentajeDesdeApi = Number(expediente?.porcentajePagado);
    const tienePorcentajeApi = !Number.isNaN(porcentajeDesdeApi);

    if (tienePorcentajeApi) {
      let estado, bgColor, textColor, borderColor;
      if (porcentajeDesdeApi >= 100) {
        estado = 'Pagado';
        bgColor = '#D1FAE5';
        textColor = '#065F46';
        borderColor = '#A7F3D0';
      } else if (porcentajeDesdeApi > 0) {
        estado = 'Parcial';
        bgColor = '#FEF3C7';
        textColor = '#92400E';
        borderColor = '#FDE68A';
      } else {
        estado = 'Pendiente';
        bgColor = '#FEE2E2';
        textColor = '#991B1B';
        borderColor = '#FECACA';
      }
      return (
        <span style={{
          backgroundColor: bgColor,
          color: textColor,
          padding: '6px 12px',
          borderRadius: '6px',
          fontSize: '0.8125rem',
          fontWeight: '600',
          border: `1px solid ${borderColor}`
        }}>
          {estado}
        </span>
      );
    }

    if (!expediente.abono || expediente.abono.length === 0 || !expediente.montoTotal) {
      return (
        <span style={{
          backgroundColor: '#FEE2E2',
          color: '#991B1B',
          padding: '6px 12px',
          borderRadius: '6px',
          fontSize: '0.8125rem',
          fontWeight: '600',
          border: '1px solid #FECACA'
        }}>
          Pendiente
        </span>
      );
    }

    const totalPagado = expediente.abono.reduce((sum, abono) => sum + abono.monto, 0);
    const porcentajePagado = (totalPagado / expediente.montoTotal) * 100;

    let estado, bgColor, textColor, borderColor;
    if (porcentajePagado >= 100) {
      estado = 'Pagado';
      bgColor = '#D1FAE5';
      textColor = '#065F46';
      borderColor = '#A7F3D0';
    } else if (porcentajePagado > 0) {
      estado = 'Parcial';
      bgColor = '#FEF3C7';
      textColor = '#92400E';
      borderColor = '#FDE68A';
    } else {
      estado = 'Pendiente';
      bgColor = '#FEE2E2';
      textColor = '#991B1B';
      borderColor = '#FECACA';
    }

    return (
      <span style={{
        backgroundColor: bgColor,
        color: textColor,
        padding: '6px 12px',
        borderRadius: '6px',
        fontSize: '0.8125rem',
        fontWeight: '600',
        border: `1px solid ${borderColor}`
      }}>
        {estado}
      </span>
    );
  };

  // Función para calcular el estado del expediente basándose en las partes
  const calcularEstadoExpediente = (expediente) => {
    if (!expediente.parte || expediente.parte.length === 0) {
      return 'PENDIENTE';
    }
    
    const partesCompletadas = expediente.parte.filter(p => p.estado === 'COMPLETADA').length;
    const totalPartes = expediente.parte.length;
    
    if (partesCompletadas === totalPartes) {
      return 'COMPLETADO';
    } else if (partesCompletadas > 0) {
      return 'EN_PROGRESO';
    } else {
      return 'PENDIENTE';
    }
  };

  const getEstadoExpedienteBadge = (estado) => {
    const badges = {
      PENDIENTE: { bgColor: '#FEF3C7', textColor: '#92400E', borderColor: '#FDE68A', text: 'Pendiente' },
      EN_PROGRESO: { bgColor: '#DBEAFE', textColor: '#1E40AF', borderColor: '#BFDBFE', text: 'En Progreso' },
      COMPLETADO: { bgColor: '#D1FAE5', textColor: '#065F46', borderColor: '#A7F3D0', text: 'Completado' },
      REASIGNADO: { bgColor: '#F3F4F6', textColor: '#374151', borderColor: '#E5E7EB', text: 'Pendiente de nuevo' }
    };
    const badge = badges[estado] || { bgColor: '#F3F4F6', textColor: '#374151', borderColor: '#E5E7EB', text: estado };
    return (
      <span style={{
        backgroundColor: badge.bgColor,
        color: badge.textColor,
        padding: '6px 12px',
        borderRadius: '6px',
        fontSize: '0.8125rem',
        fontWeight: '600',
        border: `1px solid ${badge.borderColor}`
      }}>
        {badge.text}
      </span>
    );
  };

  if (loading && !searching) {
    return (
      <div>
        {/* Header Moderno */}
        <div className="d-flex justify-content-between align-items-center mb-4" style={{ 
          padding: '24px',
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid #E5E7EB'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.875rem', fontWeight: '700', color: '#111827', letterSpacing: '-0.025em' }}>
                Expedientes
              </h2>
              <p style={{ margin: '4px 0 0 0', fontSize: '0.9375rem', color: '#6B7280' }}>
                Gestiona y consulta todos los expedientes
              </p>
            </div>
          </div>
          {user?.rol !== 'PERSONAL' && (
            <Link 
              to="/expedientes/nuevo" 
              className="btn"
              style={{
                background: '#4F46E5',
                border: 'none',
                borderRadius: '10px',
                padding: '12px 24px',
                fontWeight: '600',
                color: 'white',
                fontSize: '0.9375rem',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                textDecoration: 'none'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#4338CA';
                e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = '#4F46E5';
                e.target.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Nuevo Expediente
            </Link>
          )}
        </div>



        {/* Filtros */}
        <AdvancedFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          showAdvancedFilters={user?.rol !== 'PERSONAL'}
        />

        {/* Loading state */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '48px',
          textAlign: 'center',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid #E5E7EB'
        }}>
          <div className="spinner-border" role="status" style={{ width: '3rem', height: '3rem', color: '#4F46E5' }}>
            <span className="sr-only">Cargando expedientes...</span>
          </div>
          <p style={{ marginTop: '16px', color: '#6B7280', fontSize: '0.9375rem' }}>Cargando expedientes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        {error}
      </div>
    );
  }

  const totalPages = pagination.total > 0 ? Math.ceil(pagination.total / pagination.pageSize) : 0;

  return (
    <div>
      {/* Header Moderno */}
      <div className="d-flex justify-content-between align-items-center mb-4" style={{ 
        padding: '24px',
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        border: '1px solid #E5E7EB'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.875rem', fontWeight: '700', color: '#111827', letterSpacing: '-0.025em' }}>
              Expedientes
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.9375rem', color: '#6B7280' }}>
              {user?.rol === 'PERSONAL'
                ? 'Consulta los expedientes y el avance de tu trabajo'
                : 'Gestiona y consulta todos los expedientes'}
            </p>
          </div>
        </div>
        {user?.rol !== 'PERSONAL' && (
          <Link 
            to="/expedientes/nuevo" 
            className="btn"
            style={{
              background: '#4F46E5',
              border: 'none',
              borderRadius: '10px',
              padding: '12px 24px',
              fontWeight: '600',
              color: 'white',
              fontSize: '0.9375rem',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              textDecoration: 'none'
            }}
            onMouseOver={(e) => {
              e.target.style.background = '#4338CA';
              e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = '#4F46E5';
              e.target.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Nuevo Expediente
          </Link>
        )}
      </div>



      {/* Filtros */}
      <AdvancedFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        showAdvancedFilters={user?.rol !== 'PERSONAL'}
      />

      {/* Tabla de expedientes */}
      <div className="card">
        <div className="card-body">
          {/* Indicador de búsqueda */}
          {searching && (
            <div className="text-center mb-3">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="sr-only">Buscando...</span>
              </div>
              <span className="ml-2 text-muted">Buscando...</span>
            </div>
          )}
          
          {/* Vista MÓVIL - Cards */}
          {isMobile ? (
            <div className="mobile-cards">
              {expedientes.map((expediente) => (
                <div key={expediente.id} className="mobile-card">
                  <div className="mobile-card-header">
                    <div>
                      <strong style={{ fontSize: '1rem', color: '#2c3e50' }}>
                        {expediente.numero}
                      </strong>
                      {expediente.eliminado && (
                        <span className="badge bg-secondary ms-2" style={{ fontSize: '0.7rem' }}>Eliminado</span>
                      )}
                    </div>
                    <Link
                      to={`/expedientes/${expediente.id}`}
                      className={`btn btn-sm ${expediente.eliminado ? 'btn-outline-secondary' : 'btn-primary'}`}
                      style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                    >
                      <i className="fas fa-eye"></i> Ver
                    </Link>
                  </div>

                  <div className="mobile-card-body">
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Cliente:</span>
                      <span className="mobile-card-value">{expediente.cliente?.nombre || 'N/A'}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Tipo:</span>
                      <span className="mobile-card-value">{expediente.tipo?.nombre || 'N/A'}</span>
                    </div>
                    {canViewFinancialData && (
                      <div className="mobile-card-row">
                      <span className="mobile-card-label">{filters.estadoPago === 'debe' ? 'Deuda:' : 'Monto:'}</span>
                      <span className="mobile-card-value" style={{ fontWeight: 'bold', color: filters.estadoPago === 'debe' ? '#dc3545' : '#28a745' }}>
                        {formatCurrency(filters.estadoPago === 'debe' ? expediente.adeudado : expediente.montoTotal)}
                        </span>
                      </div>
                    )}
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Fecha de alta:</span>
                      <span className="mobile-card-value">
                        {formatDate(expediente.createdAt)}
                        {expediente.updatedAt && (
                          <span className="d-block text-muted small">
                            Últ. modificación: {formatDate(expediente.updatedAt)}
                          </span>
                        )}
                      </span>
                    </div>
                    {!hideEstadoPago && (
                      <div className="mobile-card-row">
                        <span className="mobile-card-label">Estado Pago:</span>
                        <span className="mobile-card-value">
                          {expediente.eliminado ? (
                            <span className="badge bg-secondary">Eliminado</span>
                          ) : (
                            getEstadoPagoBadge(expediente)
                          )}
                        </span>
                      </div>
                    )}
                    <div className="mobile-card-row" style={{ borderBottom: 'none' }}>
                      <span className="mobile-card-label">Estado Trabajo:</span>
                      <span className="mobile-card-value">
                        {expediente.eliminado ? (
                          <span className="badge bg-secondary">Eliminado</span>
                        ) : (
                          getEstadoExpedienteBadge(calcularEstadoExpediente(expediente))
                        )}
                      </span>
                    </div>
                  </div>

                  {user?.rol === 'JEFE' && (
                    <div className="mobile-card-actions">
                      {!expediente.eliminado ? (
                        <button
                          className="btn btn-danger"
                          onClick={() => handleDeleteClick(expediente)}
                          disabled={deletingId === expediente.id}
                          title="Eliminar Expediente"
                        >
                          <i className={deletingId === expediente.id ? "fas fa-spinner fa-spin" : "fas fa-trash-alt"}></i>
                          <span className="btn-text">
                            {deletingId === expediente.id ? 'Eliminando...' : 'Eliminar Expediente'}
                          </span>
                        </button>
                      ) : (
                        <button
                          className="btn btn-success"
                          onClick={() => handleRestoreClick(expediente)}
                          disabled={deletingId === expediente.id}
                          title="Restaurar Expediente"
                        >
                          <i className="fas fa-undo"></i>
                          <span className="btn-text">Restaurar Expediente</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            /* Vista DESKTOP - Tabla */
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>
                    <button
                      className="btn btn-link p-0"
                      onClick={() => handleSort('numero')}
                    >
                      Número
                      {sortConfig.sort === 'numero' && (
                        <i className={`fas fa-sort-${sortConfig.order === 'asc' ? 'up' : 'down'} ml-1`}></i>
                      )}
                    </button>
                  </th>
                  <th>
                    <button
                      className="btn btn-link p-0"
                      onClick={() => handleSort('cliente')}
                    >
                      Cliente
                      {sortConfig.sort === 'cliente' && (
                        <i className={`fas fa-sort-${sortConfig.order === 'asc' ? 'up' : 'down'} ml-1`}></i>
                      )}
                    </button>
                  </th>
                  <th>
                    <button
                      className="btn btn-link p-0"
                      onClick={() => handleSort('tipo')}
                    >
                      Tipo
                      {sortConfig.sort === 'tipo' && (
                        <i className={`fas fa-sort-${sortConfig.order === 'asc' ? 'up' : 'down'} ml-1`}></i>
                      )}
                    </button>
                  </th>
                  {canViewFinancialData && (
                    <th>
                      <button
                        className="btn btn-link p-0"
                        onClick={() => handleSort('montoTotal')}
                      >
                        {filters.estadoPago === 'debe' ? 'Deuda' : 'Monto'}
                        {sortConfig.sort === 'montoTotal' && (
                          <i className={`fas fa-sort-${sortConfig.order === 'asc' ? 'up' : 'down'} ml-1`}></i>
                        )}
                      </button>
                    </th>
                  )}
                  <th>
                    <button
                      className="btn btn-link p-0"
                      onClick={() => handleSort('createdAt')}
                      title="Ordena por fecha de alta (registro). La última modificación se muestra debajo."
                    >
                      Fecha de alta
                      {sortConfig.sort === 'createdAt' && (
                        <i className={`fas fa-sort-${sortConfig.order === 'asc' ? 'up' : 'down'} ml-1`}></i>
                      )}
                    </button>
                  </th>
                  {!hideEstadoPago && (
                    <th>
                      <button
                        className="btn btn-link p-0"
                        onClick={() => handleSort('estadoPago')}
                      >
                        Estado Pago
                        {sortConfig.sort === 'estadoPago' && (
                          <i className={`fas fa-sort-${sortConfig.order === 'asc' ? 'up' : 'down'} ml-1`}></i>
                        )}
                      </button>
                    </th>
                  )}
                  <th>
                    <button
                      className="btn btn-link p-0"
                      onClick={() => handleSort('estadoExpediente')}
                    >
                      Estado Expediente
                      {sortConfig.sort === 'estadoExpediente' && (
                        <i className={`fas fa-sort-${sortConfig.order === 'asc' ? 'up' : 'down'} ml-1`}></i>
                      )}
                    </button>
                  </th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {expedientes.map((expediente) => (
                  <tr 
                    key={expediente.id} 
                    className={expediente.eliminado ? 'table-secondary' : ''}
                    style={expediente.eliminado ? { opacity: 0.7 } : {}}
                  >
                    <td>
                      <strong>
                        {expediente.numero}
                        {expediente.eliminado && (
                          <span className="badge bg-secondary ms-2">Eliminado</span>
                        )}
                      </strong>
                    </td>
                    <td>{expediente.cliente?.nombre || 'N/A'}</td>
                    <td>{expediente.tipo?.nombre || 'N/A'}</td>
                    {canViewFinancialData && (
                      <td>{formatCurrency(filters.estadoPago === 'debe' ? expediente.adeudado : expediente.montoTotal)}</td>
                    )}
                    <td>
                      <div>{formatDate(expediente.createdAt)}</div>
                      {expediente.updatedAt && (
                        <div className="text-muted small">
                          Últ. modificación: {formatDate(expediente.updatedAt)}
                        </div>
                      )}
                    </td>
                    {!hideEstadoPago && (
                      <td>
                        {expediente.eliminado ? (
                          <span className="badge bg-secondary">Eliminado</span>
                        ) : (
                          getEstadoPagoBadge(expediente)
                        )}
                      </td>
                    )}
                    <td>
                      {expediente.eliminado ? (
                        <span className="badge bg-secondary">Eliminado</span>
                      ) : (
                        getEstadoExpedienteBadge(calcularEstadoExpediente(expediente))
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Link
                          to={`/expedientes/${expediente.id}`}
                          className={`btn btn-sm ${expediente.eliminado ? 'btn-outline-secondary' : 'btn-primary'}`}
                        >
                          <i className="fas fa-eye"></i> Ver
                        </Link>
                        {user?.rol === 'JEFE' && !expediente.eliminado && (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteClick(expediente)}
                            disabled={deletingId === expediente.id}
                            title="Eliminar expediente"
                            style={{
                              backgroundColor: '#dc3545',
                              borderColor: '#dc3545',
                              color: 'white',
                              fontWeight: 'bold',
                              minWidth: '40px',
                              boxShadow: '0 2px 4px rgba(220, 53, 69, 0.3)'
                            }}
                          >
                            {deletingId === expediente.id ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fas fa-trash-alt"></i>
                            )}
                          </button>
                        )}
                        {user?.rol === 'JEFE' && expediente.eliminado && (
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleRestoreClick(expediente)}
                            disabled={deletingId === expediente.id}
                            title="Restaurar expediente"
                          >
                            <i className="fas fa-undo"></i> Restaurar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          {/* Paginación */}
          {totalPages > 1 && (
            <nav>
              <ul className="pagination justify-content-center">
                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    Anterior
                  </button>
                </li>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <li key={page} className={`page-item ${page === pagination.page ? 'active' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  </li>
                ))}
                
                <li className={`page-item ${pagination.page === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === totalPages}
                  >
                    Siguiente
                  </button>
                </li>
              </ul>
            </nav>
          )}

          {/* Información de paginación */}
          <div className="text-center text-muted">
            {pagination.total > 0 ? (
              `Mostrando ${((pagination.page - 1) * pagination.pageSize) + 1} a ${Math.min(pagination.page * pagination.pageSize, pagination.total)} de ${pagination.total} expedientes`
            ) : (
              <div className="no-results">
                <i className="fas fa-search fa-2x text-muted mb-2"></i>
                <p className="text-muted">
                  {filters.search 
                    ? `No se encontraron expedientes que coincidan con "${filters.search}"`
                    : 'No hay expedientes para mostrar'
                  }
                </p>
                {filters.search && (
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => handleFilterChange({ ...filters, search: '' })}
                  >
                    Limpiar búsqueda
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && expedienteToDelete && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={handleCancelDelete}
        >
          <div 
            className="card border-danger"
            style={{
              maxWidth: '500px',
              width: '90%',
              zIndex: 10000,
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header bg-danger text-white">
              <h5 className="card-title mb-0">
                <i className="fas fa-exclamation-triangle"></i>
                {' '}Confirmar eliminación
              </h5>
            </div>
            <div className="card-body">
              <div className="text-center mb-3">
                <i className="fas fa-trash-alt fa-3x text-danger"></i>
              </div>
                              <p className="text-center">
                  ¿Estás seguro de que deseas eliminar el expediente{' '}
                  <strong className="text-danger">{expedienteToDelete.numero}</strong>?
                </p>
                <div className="alert alert-warning">
                  <i className="fas fa-info-circle"></i>
                  <strong>Información:</strong> Esta acción realizará una eliminación suave:
                  <ul className="mb-0 mt-2">
                    <li>✅ Los datos generales se conservarán</li>
                    <li>❌ Se eliminarán todas las partes asociadas</li>
                    <li>❌ Se eliminarán todos los abonos registrados</li>
                    <li>❌ Se eliminará la carpeta física y todos los archivos</li>
                    <li>✅ El expediente aparecerá en gris en la lista</li>
                    <li>✅ Podrás restaurarlo más tarde si es necesario</li>
                  </ul>
                </div>
                <p className="text-center text-warning fw-bold">
                  <i className="fas fa-info-circle"></i> Los datos generales se conservarán.
                </p>
            </div>
            <div className="card-footer d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleCancelDelete}
                disabled={deletingId === expedienteToDelete.id}
              >
                <i className="fas fa-times"></i> Cancelar
              </button>
                              <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleConfirmDelete}
                  disabled={deletingId === expedienteToDelete.id}
                >
                  {deletingId === expedienteToDelete.id ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-archive"></i>
                      Eliminar (conservar datos)
                    </>
                  )}
                </button>
            </div>
          </div>
        </div>
      )}

      {/* Estilos CSS para el botón de eliminar */}
      <style>{deleteButtonStyles}</style>
    </div>
  );
};

export default ExpedientesList;
